import { BorderlessTableOutlined } from '@ant-design/icons';
import { Button, Form, Input, Layout, message, PageHeader, Select, Space, Spin, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RiskPriority, SystemSetting } from '../../../models';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { UserContext } from '../../contexts';

export function RiskPrioritiesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);
  const [form] = useForm();

  const [systemSettings, setSystemSettings] = useState<SystemSetting[]>();
  const [riskPriorities, setRiskPriorities] = useState<RiskPriority[]>();
  const [loading, setLoading] = useState(false);
  const [systemSettingsLoading, setSystemSettingsLoading] = useState(false);
  const [riskPrioritiesLoading, setRiskPrioritiesLoading] = useState(false);
  const [priorityFormValues, setPriorityFormValues] = useState<any[]>();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    setCanEdit(hasPermission(user, 'riskPriorities.edit'));
  }, [user]);

  const loadSystemSettings = useCallback(() => {
    setSystemSettingsLoading(true);
    apiClient
      .get('system-settings')
      .then(response => {
        setSystemSettings(response.data);
        setSystemSettingsLoading(false);
      })
      .catch((reason: any) => {
        setSystemSettingsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  const loadRiskPriorities = useCallback(() => {
    setRiskPrioritiesLoading(true);
    apiClient
      .get('risk-priorities')
      .then(response => {
        setRiskPriorities(response.data);
        setRiskPrioritiesLoading(false);
      })
      .catch((reason: any) => {
        setRiskPrioritiesLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadSystemSettings();
    loadRiskPriorities();
  }, [loadRiskPriorities, loadSystemSettings]);

  useEffect(() => {
    if (!systemSettings) {
      form.setFieldsValue({ priorities: undefined });
      return;
    }

    const minProbability = systemSettings.find(m => m.id === 'MIN_PROBABILITY')?.value || '0';
    const maxProbability = systemSettings.find(m => m.id === 'MAX_PROBABILITY')?.value || '0';
    const minSeverity = systemSettings.find(m => m.id === 'MIN_SEVERITY')?.value || '0';
    const maxSeverity = systemSettings.find(m => m.id === 'MAX_SEVERITY')?.value || '0';

    const minPriorityValue = parseInt(minProbability) * parseInt(minSeverity);
    const maxPriorityValue = parseInt(maxProbability) * parseInt(maxSeverity);

    const priorityValues: number[] = [];
    for (let i = minPriorityValue; i <= maxPriorityValue; i++) {
      priorityValues.push(i);
    }

    const priorities = priorityValues.map(p => {
      const riskPriority = riskPriorities?.find(rp => rp.id === p);
      return {
        id: p,
        name: riskPriority?.name,
        color: riskPriority?.color,
      };
    });

    setPriorityFormValues(priorities);
    form.setFieldsValue({ priorities });
  }, [form, riskPriorities, systemSettings]);

  const onValueChange = (id: string) => (newValue: string) => {
    setSystemSettingsLoading(true);
    apiClient
      .put(`system-settings/${id}`, { value: newValue })
      .then(() => {
        setSystemSettingsLoading(false);
        loadSystemSettings();
      })
      .catch((reason: any) => {
        setSystemSettingsLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onEditFinish = (values: any) => {
    setLoading(true);
    apiClient
      .post('risk-priorities', values)
      .then(() => {
        message.success('Öncelikler kaydedildi.');
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <PageHeader
        title={
          <>
            <BorderlessTableOutlined /> Risk öncelikleri
          </>
        }
      />
      <Content>
        <Content style={{ backgroundColor: '#fff' }}>
          <Spin spinning={systemSettingsLoading}>
            <div style={{ marginBottom: '3rem' }}>
              <Space direction="vertical" size={'large'}>
                <Space size={'large'}>
                  <div style={{ width: '10rem' }}>
                    <Text strong>Olasılık değerleri</Text>
                  </div>
                  <div>
                    <Space>
                      <Text type={'secondary'}>En düşük:</Text>
                      <Text editable={canEdit && { onChange: onValueChange('MIN_PROBABILITY') }}>
                        {systemSettings?.find(m => m.id === 'MIN_PROBABILITY')?.value}
                      </Text>
                    </Space>
                  </div>
                  <span>-</span>
                  <div>
                    <Space>
                      <Text type={'secondary'}>En yüksek:</Text>
                      <Text editable={canEdit && { onChange: onValueChange('MAX_PROBABILITY') }}>
                        {systemSettings?.find(m => m.id === 'MAX_PROBABILITY')?.value}
                      </Text>
                    </Space>
                  </div>
                </Space>
                <Space size={'large'}>
                  <div style={{ width: '10rem' }}>
                    <Text strong>Şiddet değerleri</Text>
                  </div>
                  <div>
                    <Space>
                      <Text type={'secondary'}>En düşük:</Text>
                      <Text editable={canEdit && { onChange: onValueChange('MIN_SEVERITY') }}>
                        {systemSettings?.find(m => m.id === 'MIN_SEVERITY')?.value}
                      </Text>
                    </Space>
                  </div>
                  <span>-</span>
                  <div>
                    <Space>
                      <Text type={'secondary'}>En yüksek:</Text>
                      <Text editable={canEdit && { onChange: onValueChange('MAX_SEVERITY') }}>
                        {systemSettings?.find(m => m.id === 'MAX_SEVERITY')?.value}
                      </Text>
                    </Space>
                  </div>
                </Space>
              </Space>
            </div>
          </Spin>
          <Spin spinning={systemSettingsLoading || riskPrioritiesLoading}>
            <div>
              <Text strong>Risk puanı ve öncelikler</Text>
              {priorityFormValues && (
                <Form layout="horizontal" form={form} onFinish={onEditFinish} style={{ marginTop: '1rem' }}>
                  <Form.List name="priorities">
                    {fields => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key}>
                            <Space>
                              <Form.Item {...restField} name={[name, 'priority']}>
                                <Input type={'hidden'} />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, 'name']}
                                label={priorityFormValues[key].id}
                                rules={[{ required: true, message: 'Zorunlu alan' }]}
                              >
                                <Input type="text" placeholder="İsim" style={{ width: 200 }} disabled={!canEdit} />
                              </Form.Item>
                              <Form.Item {...restField} name={[name, 'color']} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                                <Select style={{ width: 200 }} placeholder="Renk" disabled={!canEdit}>
                                  <Select.Option value={'8fea61'}>{<span style={{ color: '#8fea61' }}>Renk 1</span>}</Select.Option>
                                  <Select.Option value={'52c41a'}>{<span style={{ color: '#52c41a' }}>Renk 2</span>}</Select.Option>
                                  <Select.Option value={'398712'}>{<span style={{ color: '#398712' }}>Renk 3</span>}</Select.Option>
                                  <Select.Option value={'66b5ff'}>{<span style={{ color: '#66b5ff' }}>Renk 4</span>}</Select.Option>
                                  <Select.Option value={'1990ff'}>{<span style={{ color: '#1990ff' }}>Renk 5</span>}</Select.Option>
                                  <Select.Option value={'0069cc'}>{<span style={{ color: '#0069cc' }}>Renk 6</span>}</Select.Option>
                                  <Select.Option value={'fcd482'}>{<span style={{ color: '#fcd482' }}>Renk 7</span>}</Select.Option>
                                  <Select.Option value={'faad14'}>{<span style={{ color: '#faad14' }}>Renk 8</span>}</Select.Option>
                                  <Select.Option value={'e19705'}>{<span style={{ color: '#e19705' }}>Renk 9</span>}</Select.Option>
                                  <Select.Option value={'ff8082'}>{<span style={{ color: '#ff8082' }}>Renk 10</span>}</Select.Option>
                                  <Select.Option value={'ff4d4f'}>{<span style={{ color: '#ff4d4f' }}>Renk 11</span>}</Select.Option>
                                  <Select.Option value={'cc0003'}>{<span style={{ color: '#cc0003' }}>Renk 12</span>}</Select.Option>
                                </Select>
                              </Form.Item>
                            </Space>
                          </div>
                        ))}
                      </>
                    )}
                  </Form.List>
                  {canEdit && (
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Kaydet
                      </Button>
                    </Form.Item>
                  )}
                </Form>
              )}
            </div>
          </Spin>
        </Content>
      </Content>
    </>
  );
}
